import {
  lazy,
  Suspense,
  useRef,
  useEffect,
  useState,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import i18n from "../../../language/i18n";
import {
  Icon,
  IconButton,
  BaseLoading,
  BaseModal,
  InputGroup,
  InputWithSpecialExpression,
  PrimaryButton,
  convertDataURLtoFile,
  useScreenSize,
  splitDiagramSegments,
} from "../../../library";
import AudioDeviceSelector from "../../common/selectors/AudioDeviceSelector";
import AiDiagram from "../../common/diagram/AiDiagram";
import { NotificationManager } from "../../../notifications/NotificationManager";
import {
  createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem,
  createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem,
} from "../../../requests/StorageRequests";
import connection_production from "../../../config/connection-production.json";
import connection_local from "../../../config/connection-local.json";
import connection_development from "../../../config/connection-development.json";
import {
  initialAudioState,
  audioReducer,
  splitTextIntoParagraphs,
  getConsecutiveAssistantMessages,
} from "../../../hooks/chatAudioUtils";
import { useAudioPlayback } from "../../../hooks/useAudioPlayback";
import { useTTSConversion } from "../../../hooks/useTTSConversion";
import { useAudioRecording } from "../../../hooks/useAudioRecording";
import { chatBubbleStyles } from "./ChatBubbleStyles";
// Lazy so the canvas (react-konva) stack loads only when the drawing modal is opened.
const Konva = lazy(() => import("../../common/konvas/Konva"));

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

// Paced reveal: how long an assistant paragraph stays on screen before the next
// one slides in, so the learner can actually read it instead of the whole reply
// dumping at once. Scales with length (kid-friendly ~160 wpm), clamped so short
// lines never feel sluggish and long ones never stall the conversation.
const computeReadingDelayMs = (text: string) => {
  const wordCount = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.min(4500, Math.max(1200, wordCount * 380));
};

const ChatBubble = ({
  showText,
  setShowText,
  chatTitle = i18n.t("chat"),
  chatSize = "md",
  // Bare/embedded variant: drops the white card + scroll surfaces so the chat
  // sits directly on the host's own card (e.g. the learning-path glass card).
  chatTransparent = false,
  chatWelcomeText,
  chatList = [],
  chatLoading = false,
  sendMessageOnClick,
  closeOnClick,
  chatCanConvertAudio = false,
  chatVoiceItemOwned = true,
  chatCanDraw = true,
  userInformation,
  userProfile,
  curriculumType,
  brandId,
  campusId,
  institutionId,
  chatActivityId,
  chatActivityTemplateId,
  chatActivityTemplateType,
  chatSubjectDescriptionId,
  chatVoiceType,
  chatVoiceLanguage,
  chatActType,
  chatPosition,
  chatErrorText,
  chatContinueText,
  chatContinueOnClick,
  // Optional "start" affordance shown under the welcome bubble before the chat
  // begins — the AI opening is deferred until the learner taps this.
  chatStartText,
  chatStartOnClick,
  chatActionList = [],
}) => {
  const { containerSize } = useScreenSize();
  const [audioState, dispatchAudio] = useReducer(
    audioReducer,
    initialAudioState,
  );
  const [chatMessage, setChatMessage] = useState("");
  const [chatMode, setChatMode] = useState("TEXT");
  const [audioItemList, setAudioItemList] = useState([]);
  const [audioUrlList, setAudioUrlList] = useState([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState("");
  const [chatItemWidth, setChatItemWidth] = useState(0);
  const [chatBottomPadding, setChatBottomPadding] = useState(0);
  const [showKonvaModal, setShowKonvaModal] = useState(false);
  const [showChatContinueButton, setShowChatContinueButton] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [drawingData, setDrawingData] = useState({
    rectangleList: [],
    circleList: [],
    arrowList: [],
    lineList: [],
    richTextList: [],
    imageList: [],
  });

  const konvaRef = useRef(null);
  const completedParagraphKeysRef = useRef(new Set());
  const animatedParagraphKeysRef = useRef(new Set());
  // Keys of assistant paragraphs that have already been paced-revealed. Paired
  // with revealVersion (a re-render trigger) and revealTimerRef (the single
  // in-flight reveal timeout) to drip newly-streamed paragraphs in one-by-one.
  const revealedParagraphKeysRef = useRef(new Set());
  const revealTimerRef = useRef(null);
  const [revealVersion, setRevealVersion] = useState(0);
  const prevCompletedCountRef = useRef(0);
  const chatComponentRef = useRef(null);
  const discardRecordingRef = useRef(() => {});
  const [chatErrorNode, setChatErrorNode] = useState(null);
  const [chatContinueNode, setChatContinueNode] = useState(null);

  const apiParams = {
    userInformation,
    userProfile,
    curriculumType,
    brandId,
    campusId,
    institutionId,
    chatActivityId,
    chatActivityTemplateId,
    chatActivityTemplateType,
    chatSubjectDescriptionId,
    chatVoiceType,
    chatVoiceLanguage,
    chatActType,
  };

  // 1. Playback hook
  const {
    audioComponentRef,
    playbackAudioContextRef,
    stopAndCleanupAudio,
    handlePausePlayback,
    handleResumePlayback,
    recordingAudioContextRef,
    recordingAnalyserRef,
    recordingDataArrayRef,
    recordingCanProceed,
    setRecordingCanProceed,
  } = useAudioPlayback({
    audioState,
    dispatchAudio,
    audioUrlList,
    setAudioUrlList,
    setAudioItemList,
    chatMode,
    selectedSpeakerId,
  });

  const handleSendMessage = useCallback(
    async (currentChatMessage?: string) => {
      if (chatLoading) return;
      if (chatErrorText) return;
      if (showChatContinueButton) return;
      if (currentChatMessage && currentChatMessage.trim()) {
        await sendMessageOnClick({ text: currentChatMessage });
      } else {
        if (!chatMessage.trim() && !imageUrl) return;
        await sendMessageOnClick({ text: chatMessage, imageUrl });
        setChatMessage("");
        setImageUrl(null);
      }

      if (chatMode === "AUDIO") {
        discardRecordingRef.current();
        setAudioUrlList([]);
        setAudioItemList([]);
      }
    },
    [
      chatLoading,
      chatErrorText,
      showChatContinueButton,
      chatMessage,
      sendMessageOnClick,
      chatMode,
      imageUrl,
    ],
  );

  // 2. Recording hook
  const {
    startRecording,
    stopRecording,
    discardRecording,
    recordingIsActive,
    streamReady,
  } = useAudioRecording({
    audioState,
    dispatchAudio,
    chatMode,
    selectedMicrophoneId,
    recordingAudioContextRef,
    recordingAnalyserRef,
    recordingDataArrayRef,
    recordingCanProceed,
    setRecordingCanProceed,
    onSendMessage: handleSendMessage,
    onError: () => {
      setChatMode("TEXT");
      stopAndCleanupAudio();
      dispatchAudio({ type: "RESET" });
    },
    apiParams,
  });

  // Keep discardRecordingRef in sync so handleSendMessage can call it
  useEffect(() => {
    discardRecordingRef.current = discardRecording;
  }, [discardRecording]);

  // 3. TTS hook
  useTTSConversion({
    audioState,
    dispatchAudio,
    chatMode,
    chatList,
    chatLoading,
    chatCanConvertAudio,
    audioItemList,
    setAudioItemList,
    audioUrlList,
    setAudioUrlList,
    stopAndCleanupAudio,
    discardRecording,
    apiParams,
    onError: () => {
      setChatMode("TEXT");
      stopAndCleanupAudio();
      dispatchAudio({ type: "RESET" });
    },
  });

  const playbackSpeedOptions = [
    { label: "1x", value: 1 },
    { label: "1.25x", value: 1.25 },
    { label: "1.5x", value: 1.5 },
    { label: "1.75x", value: 1.75 },
    { label: "2x", value: 2 },
  ];

  // Select default audio devices when entering AUDIO mode
  useEffect(() => {
    if (chatMode !== "AUDIO") return;
    if (selectedMicrophoneId && selectedSpeakerId) return;

    const selectDefaultDevices = async () => {
      let permissionStream = null;
      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const devices = await navigator.mediaDevices.enumerateDevices();

        if (!selectedMicrophoneId) {
          const mics = devices.filter((d) => d.kind === "audioinput");
          const defaultMic =
            mics.find((d) => d.deviceId === "default") || mics[0];
          if (defaultMic) setSelectedMicrophoneId(defaultMic.deviceId);
        }

        if (!selectedSpeakerId) {
          const speakers = devices.filter((d) => d.kind === "audiooutput");
          const defaultSpeaker =
            speakers.find((d) => d.deviceId === "default") || speakers[0];
          if (defaultSpeaker) setSelectedSpeakerId(defaultSpeaker.deviceId);
        }
      } catch (e) {
        console.warn("Could not select default audio devices:", e);
      } finally {
        if (permissionStream) {
          permissionStream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch (err) {}
          });
        }
      }
    };

    selectDefaultDevices();
  }, [chatMode, selectedMicrophoneId, selectedSpeakerId]);

  // When chat mode changes, mark all existing paragraphs as completed so they don't re-animate
  // and scroll to bottom after the new container mounts
  useEffect(() => {
    animatedParagraphKeysRef.current.clear();
    // A pending paced-reveal belongs to the mode we're leaving — drop it so it
    // can't fire against the freshly rebuilt (differently-keyed) paragraph list.
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    chatList.forEach((chat, chatIndex) => {
      if (!chat.text) return;
      const paras = chat.text.split(/\n\n/).filter((p) => p.length > 0);
      paras.forEach((_, paraIndex) => {
        completedParagraphKeysRef.current.add(`${chatIndex}-${paraIndex}`);
      });
    });

    // Scroll to bottom after new container mounts
    requestAnimationFrame(() => {
      if (chatComponentRef.current) {
        chatComponentRef.current.scrollTop =
          chatComponentRef.current.scrollHeight;
      }
    });
  }, [chatMode]);

  useEffect(() => {
    if (chatContinueText) {
      setShowChatContinueButton(true);
    } else {
      setShowChatContinueButton(false);
    }
  }, [chatContinueText]);

  useEffect(() => {
    const calculateBottomPadding = () => {
      let totalHeight = 0;

      if (chatErrorText && chatErrorNode) {
        const errorRect = chatErrorNode.getBoundingClientRect();
        totalHeight += errorRect.height;
      }

      if (chatContinueText && chatContinueNode) {
        const continueRect = chatContinueNode.getBoundingClientRect();
        totalHeight += continueRect.height;
      }

      const extraSpacing = totalHeight > 0 ? 2 : 0;
      setChatBottomPadding(totalHeight + extraSpacing);
    };

    calculateBottomPadding();

    const observer = new ResizeObserver(() => {
      calculateBottomPadding();
    });

    if (chatErrorNode) {
      observer.observe(chatErrorNode);
    }
    if (chatContinueNode) {
      observer.observe(chatContinueNode);
    }

    return () => {
      observer.disconnect();
    };
  }, [
    chatErrorText,
    chatErrorNode,
    chatContinueText,
    chatContinueNode,
    showChatContinueButton,
  ]);

  useEffect(() => {
    if (chatSize !== "full") return;
    if (chatMode !== "TEXT") return;
    if (!chatComponentRef) return;
    if (!chatComponentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;
        setChatItemWidth(width * 0.75);
      }
    });

    resizeObserver.observe(chatComponentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [chatSize, chatMode, chatComponentRef]);

  useEffect(() => {
    if (!chatComponentRef.current) return;

    // Track reveals too, not just completions — paced-revealed paragraphs enter
    // the DOM after their text completes, so the scroll must follow the reveal.
    const currentCount =
      completedParagraphKeysRef.current.size +
      revealedParagraphKeysRef.current.size;
    if (currentCount !== prevCompletedCountRef.current) {
      prevCompletedCountRef.current = currentCount;
      const chatElement = chatComponentRef.current;
      chatElement.scrollTo({
        top: chatElement.scrollHeight,
        behavior: "smooth",
      });
    }
  });

  useEffect(() => {
    if (
      chatMode === "AUDIO" &&
      audioState.isPlaying &&
      !audioState.isPaused &&
      chatComponentRef.current
    ) {
      const highlightedElement = chatComponentRef.current.querySelector(
        ".highlighted-sentence",
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [
    audioState.activePlayIndex,
    audioState.isPlaying,
    audioState.isPaused,
    chatMode,
  ]);

  // Activity reset effect
  useEffect(() => {
    stopAndCleanupAudio();
    discardRecording();
    setAudioUrlList([]);
    setAudioItemList([]);
    dispatchAudio({ type: "RESET" });
  }, [
    chatActivityId,
    chatActivityTemplateId,
    chatActivityTemplateType,
    chatSubjectDescriptionId,
    stopAndCleanupAudio,
    discardRecording,
  ]);

  // Mode-change AudioContext resume/suspend effect
  useEffect(() => {
    if (!playbackAudioContextRef.current) return;

    if (chatMode === "AUDIO") {
      if (playbackAudioContextRef.current.state !== "closed") {
        playbackAudioContextRef.current.resume().catch((err) => {
          console.error("Failed to resume AudioContext:", err);
        });
      }
    } else {
      if (!recordingIsActive) return;
      playbackAudioContextRef.current.suspend().catch((err) => {
        console.error("Failed to suspend AudioContext:", err);
      });
      stopAndCleanupAudio();
      discardRecording();
    }
  }, [
    chatMode,
    stopAndCleanupAudio,
    discardRecording,
    playbackAudioContextRef,
    recordingIsActive,
  ]);

  const handleShowKonvaOnClick = useCallback(async () => {
    if (chatLoading) return;
    if (chatErrorText) return;
    if (showChatContinueButton) return;
    setShowKonvaModal(true);
  }, [chatLoading, chatErrorText, showChatContinueButton]);

  const handleOnChange = (event) => {
    setChatMessage(event.target.value);
  };

  const handleChatModeOnChange = useCallback(() => {
    // Voice mode is enabled for the org, but the learner has no voice yet —
    // point them to the Market instead of entering AUDIO mode.
    if (!chatVoiceItemOwned) {
      NotificationManager.info(i18n.t("mascot_voice_requires_market_item"));
      return;
    }
    setChatMode((prevMode) => {
      if (prevMode === "AUDIO") {
        stopAndCleanupAudio();
        discardRecording();
        setAudioUrlList([]);
        setAudioItemList([]);
        dispatchAudio({ type: "RESET" });
        return "TEXT";
      }
      return "AUDIO";
    });
  }, [chatVoiceItemOwned, stopAndCleanupAudio, discardRecording]);

  const interruptAndStartRecording = useCallback(() => {
    // Don't interrupt while our own voice is being processed
    if (audioState.isWaitingForVoiceConversion) return;
    // Stop everything — playback, conversion, pending items
    if (
      audioState.isPlaying ||
      audioState.isPaused ||
      audioState.isConverting ||
      audioState.isWaitingForAssistantVoice
    ) {
      stopAndCleanupAudio();
      dispatchAudio({ type: "RESET" });
    }
    // Ensure recording can proceed — previous cycle may not have reset yet
    setRecordingCanProceed(true);
    startRecording();
  }, [
    audioState.isPlaying,
    audioState.isPaused,
    audioState.isConverting,
    audioState.isWaitingForAssistantVoice,
    audioState.isWaitingForVoiceConversion,
    stopAndCleanupAudio,
    dispatchAudio,
    setRecordingCanProceed,
    startRecording,
  ]);

  const isHoldingRef = useRef(false);
  const holdTimerRef = useRef(null);
  const interruptAndStartRecordingRef = useRef(interruptAndStartRecording);
  const MIN_HOLD_DURATION = 150;

  useEffect(() => {
    interruptAndStartRecordingRef.current = interruptAndStartRecording;
  }, [interruptAndStartRecording]);

  const handleHoldToTalkStart = useCallback(() => {
    if (chatLoading) return;
    if (!streamReady) return;
    isHoldingRef.current = true;
    // Don't start recording immediately — wait for minimum hold duration
    holdTimerRef.current = setTimeout(() => {
      if (isHoldingRef.current) {
        interruptAndStartRecordingRef.current();
      }
    }, MIN_HOLD_DURATION);
  }, [chatLoading, streamReady]);

  const handleHoldToTalkEnd = useCallback(() => {
    if (!isHoldingRef.current) return;
    isHoldingRef.current = false;

    // If released before MIN_HOLD_DURATION, cancel — recording never started
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    // Only stop if recording actually started
    if (recordingIsActive) {
      stopRecording();
    }
  }, [stopRecording, recordingIsActive]);

  // Global mouseup/touchend listener for hold-to-talk release
  useEffect(() => {
    if (chatMode !== "AUDIO") return;

    const handleGlobalUp = () => {
      if (!isHoldingRef.current) return;
      isHoldingRef.current = false;

      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }

      if (recordingIsActive) {
        stopRecording();
      }
    };

    window.addEventListener("mouseup", handleGlobalUp);
    window.addEventListener("touchend", handleGlobalUp);

    return () => {
      window.removeEventListener("mouseup", handleGlobalUp);
      window.removeEventListener("touchend", handleGlobalUp);
    };
  }, [chatMode, stopRecording, recordingIsActive]);

  // Keyboard shortcut: hold Space to talk in AUDIO mode
  const isKeyHoldingRef = useRef(false);
  const keyHoldTimerRef = useRef(null);

  useEffect(() => {
    if (chatMode !== "AUDIO") return;

    const handleKeyDown = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      if (isKeyHoldingRef.current) return;
      if (chatLoading) return;
      if (!streamReady) return;
      e.preventDefault();
      isKeyHoldingRef.current = true;

      keyHoldTimerRef.current = setTimeout(() => {
        if (isKeyHoldingRef.current) {
          interruptAndStartRecordingRef.current();
        }
      }, MIN_HOLD_DURATION);
    };

    const handleKeyUp = (e) => {
      if (e.code !== "Space") return;
      if (!isKeyHoldingRef.current) return;
      isKeyHoldingRef.current = false;
      e.preventDefault();

      if (keyHoldTimerRef.current) {
        clearTimeout(keyHoldTimerRef.current);
        keyHoldTimerRef.current = null;
      }

      if (recordingIsActive) {
        stopRecording();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      if (keyHoldTimerRef.current) {
        clearTimeout(keyHoldTimerRef.current);
      }
    };
  }, [chatMode, chatLoading, recordingIsActive, stopRecording, streamReady]);

  const handleAfterCompleteOnClick = async (dataUri, drawingData) => {
    const imageFile = convertDataURLtoFile(
      dataUri,
      `${new Date().getTime()}.jpeg`,
    );
    const formData = new FormData();
    formData.append("file", imageFile);
    formData.append("fileType", "IMAGE");
    formData.append("curriculumType", curriculumType);
    formData.append("path", `/${userInformation.id}/${userProfile.id}/images`);
    formData.append("userId", userInformation.id);
    formData.append("userProfileId", userProfile.id);

    let activityTemplateChatId = -1;
    let activityTemplateChatItemId = -1;
    let subjectDescriptionChatId = -1;
    let subjectDescriptionChatItemId = -1;

    if (chatSubjectDescriptionId) {
      formData.append("subjectDescriptionId", chatSubjectDescriptionId);
      formData.append(
        "subjectDescriptionChatId",
        subjectDescriptionChatId as any,
      );
      formData.append(
        "subjectDescriptionChatItemId",
        subjectDescriptionChatItemId as any,
      );
    } else {
      formData.append("activityTemplateChatId", activityTemplateChatId as any);
      formData.append(
        "activityTemplateChatItemId",
        activityTemplateChatItemId as any,
      );
      formData.append("activityId", chatActivityId);
      formData.append("activityTemplateId", chatActivityTemplateId);
    }

    try {
      let uploadData;
      let uploadErr;

      if (chatSubjectDescriptionId) {
        ({ data: uploadData, err: uploadErr } =
          await createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem(
            formData,
          ));
      } else {
        ({ data: uploadData, err: uploadErr } =
          await createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem(
            formData,
          ));
      }

      if (uploadErr) throw uploadErr;

      const imageUrl = `${connection.base.storage.url}/storage-files/user-profile/preview/file${uploadData.path}`;
      setImageUrl(imageUrl);
      setDrawingData(drawingData);
      setShowKonvaModal(false);
    } catch (error) {
      NotificationManager.error("Failed to upload image. Please try again.");
    }
    setShowKonvaModal(false);
  };

  const handleConvaContentModalOnClose = () => {
    if (konvaRef.current?.hasDrawings()) {
      if (
        !window.confirm(
          i18n.t("are_you_sure_do_you_want_to_close_drawing_text"),
        )
      )
        return;
    }
    if (konvaRef.current) {
      const currentDrawingData = konvaRef.current.getCurrentDrawingData();
      setDrawingData(currentDrawingData);
    }
    setShowKonvaModal(false);
  };

  const getPositionStyles = () => {
    if (chatSize === "full") {
      return {};
    }

    const baseStyles = {
      top: -chatDimensions[chatSize].height - 10,
    };

    if (chatPosition === "RIGHT") {
      return {
        ...baseStyles,
        left: chatDimensions[chatSize].width / 2.5,
      };
    } else {
      return {
        ...baseStyles,
        right: chatDimensions[chatSize].width / 2.5,
      };
    }
  };

  const chatDimensions = {
    full: { width: "100%", height: "100%" },
    md: { width: 400, height: 500 },
    lg: { width: 500, height: 600 },
    xl: { width: 600, height: 700 },
  };
  const chatPositionStyle = getPositionStyles();
  const lastChat = chatList[chatList.length - 1];
  const showCanUseVoiceMode =
    chatCanConvertAudio &&
    (!lastChat || !!lastChat.id) &&
    !chatErrorText &&
    !showChatContinueButton;

  // Build the flat paragraph list once per data change (instead of inside the
  // render body) so the paced-reveal effect below and the JSX render agree on
  // the exact same paragraph set, keys, and order.
  const chatRenderData = useMemo(() => {
    const consecutiveAssistantMessages =
      getConsecutiveAssistantMessages(chatList);
    const firstAssistantIndex =
      chatList.length - consecutiveAssistantMessages.length;

    const isPartOfLastAssistantGroup = (index) => {
      if (chatMode !== "AUDIO" || audioItemList.length === 0) return false;
      if (consecutiveAssistantMessages.length === 0) return false;
      return (
        index >= firstAssistantIndex && chatList[index].role === "ASSISTANT"
      );
    };

    const getParagraphsForMessage = (messageText, messageIndex) => {
      if (chatMode !== "AUDIO" || !isPartOfLastAssistantGroup(messageIndex)) {
        return null;
      }
      if (!messageText) return null;

      const messagePositionInGroup = messageIndex - firstAssistantIndex;
      if (messagePositionInGroup < 0) return null;

      let paragraphOffset = 0;
      for (let i = 0; i < messagePositionInGroup; i++) {
        paragraphOffset += splitTextIntoParagraphs(
          consecutiveAssistantMessages[i].text,
        ).length;
      }

      const paragraphs = splitTextIntoParagraphs(messageText);
      if (paragraphs.length === 0) return null;

      return paragraphs.map((text, i) => ({
        text,
        globalIndex: paragraphOffset + i,
      }));
    };

    const list = [];
    chatList.forEach((chat, chatIndex) => {
      const { role, text, imageUrl } = chat;
      const isLastMessage = chatIndex === chatList.length - 1;
      const isStreaming =
        isLastMessage && role === "ASSISTANT" && chatLoading && !chat.id;
      const prevChat = chatIndex > 0 ? chatList[chatIndex - 1] : null;
      const showPrevImage = prevChat && prevChat.imageUrl && !imageUrl;
      const audioParagraphs = getParagraphsForMessage(text, chatIndex);

      if (!text) return;

      if (audioParagraphs) {
        // Audio mode: each paragraph is a separate item
        audioParagraphs.forEach((para, paraIndex) => {
          list.push({
            key: `${chatIndex}-${paraIndex}`,
            role,
            text: para.text,
            globalIndex: para.globalIndex,
            paraIndex,
            isAudio: true,
            imageUrl: paraIndex === 0 ? imageUrl : null,
            showPrevImage: paraIndex === 0 ? showPrevImage : false,
            prevChat,
            isStreaming: false,
          });
        });
      } else {
        // Text mode: split into paragraphs
        const paras = text.split(/\n\n/).filter((p) => p.length > 0);
        paras.forEach((para, paraIndex) => {
          const isLastPara = paraIndex === paras.length - 1;
          list.push({
            key: `${chatIndex}-${paraIndex}`,
            role,
            text: para,
            paraIndex,
            isAudio: false,
            imageUrl: paraIndex === 0 ? imageUrl : null,
            showPrevImage: paraIndex === 0 ? showPrevImage : false,
            prevChat,
            isStreaming: isStreaming && isLastPara,
          });
        });
      }
    });

    // Mark newly completed paragraphs — first appearance (not history, which was
    // pre-seeded on mount/mode-change) flags them for the slide-in + paced reveal.
    list.forEach((item) => {
      if (item.isStreaming) return;
      if (!completedParagraphKeysRef.current.has(item.key)) {
        completedParagraphKeysRef.current.add(item.key);
        animatedParagraphKeysRef.current.add(item.key);
      }
    });

    const hasStreamingParagraph = list.some((item) => item.isStreaming);

    // Only newly-streamed assistant TEXT paragraphs are paced-revealed (audio
    // paragraphs follow playback; user messages and history show immediately).
    const revealableKeys = list
      .filter(
        (item) =>
          !item.isStreaming &&
          !item.isAudio &&
          item.role === "ASSISTANT" &&
          animatedParagraphKeysRef.current.has(item.key),
      )
      .map((item) => ({ key: item.key, text: item.text }));

    return { paragraphList: list, hasStreamingParagraph, revealableKeys };
  }, [chatList, chatLoading, chatMode, audioItemList.length]);

  // Paced reveal driver: drip the queued assistant paragraphs in one at a time.
  // The first paragraph of a reply appears promptly; each later paragraph of the
  // SAME reply waits long enough to read the previous one. This decouples the
  // reveal cadence from the raw token-stream speed, which was flushing every
  // paragraph the instant a "\n\n" arrived (the "coming-coming-coming" burst).
  useEffect(() => {
    const { revealableKeys } = chatRenderData;
    const firstPendingIndex = revealableKeys.findIndex(
      (item) => !revealedParagraphKeysRef.current.has(item.key),
    );
    if (firstPendingIndex === -1) return;
    // One reveal in flight at a time; the timer survives streaming re-renders so
    // its cleanup must NOT clear it here (see the dedicated unmount effect).
    if (revealTimerRef.current) return;

    const next = revealableKeys[firstPendingIndex];
    const prev =
      firstPendingIndex > 0 ? revealableKeys[firstPendingIndex - 1] : null;
    // key is `${chatIndex}-${paraIndex}` — same chatIndex ⇒ same assistant reply.
    const sameMessage =
      prev && prev.key.split("-")[0] === next.key.split("-")[0];
    const delay = sameMessage ? computeReadingDelayMs(prev.text) : 250;

    revealTimerRef.current = setTimeout(() => {
      revealTimerRef.current = null;
      revealedParagraphKeysRef.current.add(next.key);
      setRevealVersion((version) => version + 1);
    }, delay);
  }, [chatRenderData, revealVersion]);

  // Cancel any in-flight reveal timer on unmount.
  useEffect(() => {
    return () => {
      if (revealTimerRef.current) {
        clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, []);

  const renderConvaContent = () => {
    return (
      <BaseModal
        title={i18n.t("drawing_tool")}
        icon="🎨"
        isOpen={showKonvaModal}
        onAfterOpen={() => {}}
        size="medium"
        onRequestClose={handleConvaContentModalOnClose}
      >
        <Suspense fallback={null}>
          <Konva
            ref={konvaRef}
            initialItemList={[]}
            initialRectangleList={drawingData.rectangleList || []}
            initialCircleList={drawingData.circleList || []}
            initialArrowList={drawingData.arrowList || []}
            initialLineList={drawingData.lineList || []}
            initialWidth={containerSize.width}
            handleAfterCompleteOnClick={handleAfterCompleteOnClick}
          />
        </Suspense>
      </BaseModal>
    );
  };

  const renderChatContent = () => {
    return (
      <>
        {chatWelcomeText ? (
          <div className="qbz-welcome-row anim-message-slide-in">
            <img
              className="qbz-ava"
              src="/mascots/catchy-avatar.png"
              alt=""
              aria-hidden="true"
            />
            <div
              className="qbz-welcome-bubble"
              style={{
                maxWidth:
                  chatSize === "full"
                    ? chatItemWidth
                    : chatDimensions[chatSize].width * 0.75,
                background: "#F7FBFC",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(15, 72, 89, 0.10)",
                boxShadow: "0 1px 2px rgba(15, 72, 89, 0.08)",
              }}
            >
              <p className="qbz-welcome-text">{chatWelcomeText}</p>
            </div>
          </div>
        ) : null}
        {chatStartOnClick && chatStartText && chatList.length === 0 ? (
          <div className="qbz-start-row anim-message-slide-in">
            <PrimaryButton
              title={chatStartText}
              size="medium"
              onClick={chatStartOnClick}
              disabled={chatLoading}
            />
          </div>
        ) : null}
        {(() => {
          const { paragraphList, hasStreamingParagraph, revealableKeys } =
            chatRenderData;
          const hasPendingReveal = revealableKeys.some(
            (item) => !revealedParagraphKeysRef.current.has(item.key),
          );
          // Keep Catchy's "typing" cursor up while a reply is still streaming OR
          // while paced-reveal still has queued paragraphs to slide in.
          const showStreamingCursor = hasStreamingParagraph || hasPendingReveal;

          return (
            <>
              {paragraphList.map((item) => {
                // Hide paragraphs still being streamed
                if (item.isStreaming) return null;

                // Class stays permanently — animation plays once on mount,
                // `both` fill mode keeps opacity:1 after completion
                const shouldAnimate = animatedParagraphKeysRef.current.has(
                  item.key,
                );

                // Newly-streamed assistant text paragraphs wait their turn in the
                // paced-reveal queue; keep them out of the DOM until revealed so
                // the whole reply doesn't dump in one burst.
                const isPacedReveal =
                  shouldAnimate && item.role === "ASSISTANT" && !item.isAudio;
                if (
                  isPacedReveal &&
                  !revealedParagraphKeysRef.current.has(item.key)
                ) {
                  return null;
                }

                return (
                  <div
                    className={`qbz-msg-row ${item.role === "ASSISTANT" ? "qbz-justify-start" : "qbz-justify-end"} ${shouldAnimate ? "anim-message-slide-in" : ""}`}
                    key={item.key}
                  >
                    {item.role === "ASSISTANT" ? (
                      <img
                        className="qbz-ava"
                        src="/mascots/catchy-avatar.png"
                        alt=""
                        aria-hidden="true"
                      />
                    ) : null}
                    <div
                      className={`${
                        item.role === "ASSISTANT"
                          ? "qbz-msg-assistant"
                          : "qbz-msg-user"
                      } qbz-msg-bubble`}
                      style={{
                        maxWidth:
                          chatSize === "full"
                            ? chatItemWidth
                            : chatDimensions[chatSize].width * 0.75,
                        ...(item.role === "ASSISTANT"
                          ? {
                              // No backdrop-filter — one bubble per assistant
                              // paragraph inside the scrolling chat; per-item
                              // blur layers starve tile raster.
                              background: "#F7FBFC",
                              border: "1px solid rgba(15, 72, 89, 0.10)",
                              boxShadow: "0 1px 2px rgba(15, 72, 89, 0.08)",
                            }
                          : {
                              background: "var(--catchup-blue-500)",
                              boxShadow: "0 2px 12px rgba(15, 137, 156, 0.3)",
                            }),
                      }}
                    >
                      <div className="qbz-msg-inner">
                        {item.showPrevImage ? (
                          <div
                            className="qbz-image-wrap"
                            style={{ width: "50%" }}
                          >
                            {item.prevChat.imageUrl ? (
                              <img
                                src={item.prevChat.imageUrl}
                                alt="image"
                                className="qbz-image"
                                style={{ objectFit: "contain" }}
                              />
                            ) : null}
                          </div>
                        ) : null}

                        {item.imageUrl ? (
                          <div
                            className="qbz-image-wrap"
                            style={{ width: "50%" }}
                          >
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt="image"
                                className="qbz-image"
                                style={{ objectFit: "contain" }}
                              />
                            ) : null}
                          </div>
                        ) : null}

                        <div
                          className={`qbz-text-block ${
                            item.imageUrl || item.showPrevImage
                              ? ""
                              : "qbz-text-flex-1"
                          }`}
                          style={
                            item.imageUrl || item.showPrevImage
                              ? { width: "50%" }
                              : {}
                          }
                        >
                          {item.isAudio ? (
                            <div
                              className={`qbz-audio-para ${
                                item.globalIndex === audioState.activePlayIndex
                                  ? "qbz-audio-active highlighted-sentence"
                                  : item.globalIndex <
                                      audioState.activePlayIndex
                                    ? "qbz-audio-played"
                                    : "qbz-audio-pending"
                              }`}
                              style={
                                item.globalIndex === audioState.activePlayIndex
                                  ? {
                                      background: "rgba(87, 194, 211, 0.15)",
                                      border:
                                        "1px solid rgba(87, 194, 211, 0.3)",
                                    }
                                  : {}
                              }
                            >
                              <div className="qbz-audio-para-inner">
                                <div className="qbz-audio-para-text">
                                  <InputWithSpecialExpression
                                    value={item.text}
                                    showSpecialOnly={false}
                                  />
                                </div>
                                {item.globalIndex ===
                                  audioState.activePlayIndex &&
                                  (audioState.isPlaying ||
                                    audioState.isPaused) && (
                                    <IconButton
                                      name={
                                        audioState.isPaused ? "play" : "pause"
                                      }
                                      size="small"
                                      onClick={
                                        audioState.isPaused
                                          ? handleResumePlayback
                                          : handlePausePlayback
                                      }
                                    />
                                  )}
                              </div>
                            </div>
                          ) : (
                            splitDiagramSegments(item.text).map((seg, segIndex) =>
                              seg.type === "text" ? (
                                <InputWithSpecialExpression
                                  key={`t:${segIndex}`}
                                  value={seg.value}
                                  showSpecialOnly={false}
                                />
                              ) : (
                                <AiDiagram
                                  key={`d:${segIndex}:${seg.description}`}
                                  description={seg.description}
                                  pending={!seg.complete}
                                />
                              ),
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {showStreamingCursor && (
                <div className="qbz-streaming-row">
                  <img
                    className="qbz-ava"
                    src="/mascots/catchy-avatar.png"
                    alt=""
                    aria-hidden="true"
                  />
                  <div
                    className="qbz-streaming-bubble"
                    style={{
                      background: "#F7FBFC",
                      backdropFilter: "blur(12px)",
                      border: "1px solid rgba(15, 72, 89, 0.10)",
                    }}
                  >
                    <span className="qbz-cursor anim-blink" />
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </>
    );
  };

  const renderPlaybackSpeedSelector = () => {
    return (
      <div
        className="qbz-speed-selector"
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(15, 72, 89, 0.10)",
          boxShadow: "0 4px 16px rgba(9, 101, 116, 0.1)",
        }}
      >
        <p className="qbz-speed-title">{i18n.t("playback_speed")}</p>
        <div className="qbz-speed-list">
          {playbackSpeedOptions.map((option) => (
            <button
              key={option.value}
              onClick={() =>
                dispatchAudio({
                  type: "SET_PLAYBACK_RATE",
                  payload: option.value,
                })
              }
              className={`qbz-speed-option ${
                audioState.playbackRate === option.value
                  ? "qbz-speed-option-active"
                  : "qbz-speed-option-inactive"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{chatBubbleStyles}</style>
      {renderConvaContent()}
      <div
        className={chatSize === "full" ? "qbz-root-full" : "qbz-root-absolute"}
        style={chatPositionStyle}
      >
        {(chatSize === "full" || showText) && (
          <div
            className={`qbz-card${chatTransparent ? " qbz-card--bare" : ""}`}
            style={chatDimensions[chatSize]}
          >
            <div className="qbz-card-inner">
              {/* chatTitle: null hides the title; the header row collapses entirely
                  when it would carry no title, no voice toggle and no close button */}
              {chatSize === "full" &&
              !chatTitle &&
              !showCanUseVoiceMode &&
              !closeOnClick ? null : (
                <div className="qbz-header">
                  {showCanUseVoiceMode ? (
                    <div className="qbz-header-left" style={{ top: 16 }}>
                      <Icon
                        name={
                          chatMode === "TEXT"
                            ? "start-audio"
                            : "start-audio-red"
                        }
                        size={24}
                        onClick={handleChatModeOnChange}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  ) : null}

                  {chatSize === "full" ? (
                    closeOnClick ? (
                      <div className="qbz-header-right" style={{ top: 16 }}>
                        <div className="qbz-row">
                          <IconButton
                            name="cross-red"
                            size="small"
                            onClick={() => {
                              closeOnClick();
                            }}
                          />
                        </div>
                      </div>
                    ) : null
                  ) : (
                    <div className="qbz-header-right" style={{ top: 16 }}>
                      <div className="qbz-row">
                        <IconButton
                          name="cross-red"
                          size="small"
                          onClick={() => {
                            setShowText(false);
                            discardRecording();
                            if (chatMode === "AUDIO") {
                              setAudioUrlList([]);
                              setAudioItemList([]);
                              stopAndCleanupAudio();
                              dispatchAudio({ type: "RESET" });
                              setChatMode("TEXT");
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {chatTitle ? <p className="qbz-title">{chatTitle}</p> : null}
                </div>
              )}

              {chatMode === "TEXT" ? (
                <div className="qbz-text-body">
                  {imageUrl ? (
                    <>
                      <div className="qbz-image-preview-wrap">
                        <div className="qbz-image-preview-rel">
                          <img
                            src={imageUrl}
                            alt="draw"
                            className="qbz-image-preview"
                          />
                          <div className="qbz-image-remove">
                            <IconButton
                              name="remove-red"
                              size="xsmall"
                              onClick={() => {
                                if (konvaRef.current) {
                                  const currentDrawingData =
                                    konvaRef.current.getCurrentDrawingData();
                                  setDrawingData(currentDrawingData);
                                }
                                setImageUrl(null);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div
                    className="qbz-scroll"
                    style={{
                      paddingBottom: `${chatBottomPadding}px`,
                      transition: "padding-bottom 0.3s ease-in-out",
                      background: chatTransparent
                        ? "transparent"
                        : "var(--catchup-white)",
                      border: chatTransparent
                        ? "none"
                        : "1px solid rgba(9, 101, 116, 0.06)",
                    }}
                    ref={chatComponentRef}
                  >
                    <div className="qbz-scroll-inner">
                      {renderChatContent()}
                    </div>
                  </div>
                  {chatErrorText ? (
                    <div
                      ref={setChatErrorNode}
                      className="qbz-overlay"
                      style={{ width: chatDimensions[chatSize].width * 0.85 }}
                    >
                      <div
                        className="qbz-error-box"
                        style={{
                          background: "rgba(255, 255, 255, 0.9)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(255, 107, 107, 0.3)",
                          boxShadow: "0 2px 12px rgba(255, 107, 107, 0.1)",
                        }}
                      >
                        <Icon name="exclamation-red" size="medium" />
                        <div className="qbz-error-text">
                          <p className="">{i18n.t(chatErrorText)}</p>
                        </div>
                      </div>
                    </div>
                  ) : chatContinueText ? (
                    <div
                      ref={setChatContinueNode}
                      className="qbz-overlay"
                      style={{ width: chatDimensions[chatSize].width * 0.85 }}
                    >
                      <div
                        className="qbz-continue-box"
                        style={{
                          background: "rgba(255, 255, 255, 0.9)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(15, 72, 89, 0.10)",
                          boxShadow: "0 1px 2px rgba(15, 72, 89, 0.08)",
                        }}
                      >
                        <div className="qbz-continue-text">
                          <p className="">{i18n.t(chatContinueText)}</p>
                        </div>
                        {showChatContinueButton ? (
                          <PrimaryButton
                            title={i18n.t("continue")}
                            size="small"
                            onClick={() => {
                              setShowChatContinueButton(false);
                              if (chatContinueOnClick) {
                                chatContinueOnClick();
                              }
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : chatActionList && chatActionList.length > 0 ? (
                    <div
                      className="qbz-overlay"
                      style={{ width: chatDimensions[chatSize].width * 0.85 }}
                    >
                      <div
                        className="qbz-action-box"
                        style={{
                          background: "rgba(255, 255, 255, 0.9)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(15, 72, 89, 0.10)",
                          boxShadow: "0 1px 2px rgba(15, 72, 89, 0.08)",
                        }}
                      >
                        {chatActionList.map((action, idx) => (
                          <button
                            key={action.key || idx}
                            onClick={() => {
                              if (action.onClick) action.onClick();
                            }}
                            className="qbz-action-button"
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className="qbz-input-row">
                    {!chatCanDraw ||
                    imageUrl ||
                    chatErrorText ||
                    showChatContinueButton ||
                    (chatActionList && chatActionList.length > 0) ? null : (
                      <div
                        className={`qbz-draw-toggle ${
                          chatMessage?.length === 0
                            ? "qbz-draw-toggle-visible"
                            : "qbz-draw-toggle-hidden"
                        }`}
                      >
                        <IconButton
                          name="draw"
                          size="small"
                          onClick={() => {
                            handleShowKonvaOnClick();
                          }}
                        />
                      </div>
                    )}

                    <div
                      className={`${chatMessage?.length > 0 ? "qbz-input-wrap-mr" : ""} ${
                        imageUrl ? "" : "qbz-input-wrap-ml"
                      } qbz-input-wrap`}
                    >
                      <InputGroup
                        type="text"
                        limit={150}
                        value={chatMessage}
                        disabled={chatErrorText || showChatContinueButton}
                        onChange={handleOnChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSendMessage();
                          }
                        }}
                      />
                    </div>
                    <div
                      className={`qbz-send-toggle ${
                        chatMessage?.length > 0 || imageUrl
                          ? "qbz-send-toggle-visible"
                          : "qbz-send-toggle-hidden"
                      }`}
                    >
                      <IconButton
                        name="send"
                        size="small"
                        onClick={() => {
                          handleSendMessage();
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : chatMode === "AUDIO" ? (
                <div className="qbz-audio-body">
                  <div
                    className="qbz-audio-scroll"
                    style={{
                      background: chatTransparent
                        ? "transparent"
                        : "var(--catchup-white)",
                      border: chatTransparent
                        ? "none"
                        : "1px solid rgba(9, 101, 116, 0.06)",
                    }}
                    ref={chatComponentRef}
                  >
                    <div className="qbz-scroll-inner">
                      {renderChatContent()}
                    </div>
                  </div>

                  <div
                    className="qbz-audio-controls"
                    style={{ borderTop: "1px solid rgba(9, 101, 116, 0.08)" }}
                  >
                    <div className="qbz-audio-toolbar">
                      <div className="qbz-audio-tool">
                        <IconButton
                          name="settings"
                          size="small"
                          onClick={() =>
                            dispatchAudio({
                              type: "TOGGLE_DEVICE_SELECTOR",
                            })
                          }
                        />
                        {audioState.showDeviceSelector && (
                          <div className="qbz-device-popover">
                            <AudioDeviceSelector
                              selectedMicrophoneId={selectedMicrophoneId}
                              selectedSpeakerId={selectedSpeakerId}
                              onSelectMicrophone={setSelectedMicrophoneId}
                              onSelectSpeaker={setSelectedSpeakerId}
                            />
                          </div>
                        )}
                      </div>

                      <div className="qbz-audio-tool">
                        <Icon
                          name="speed"
                          size={24}
                          onClick={() =>
                            dispatchAudio({
                              type: "TOGGLE_SPEED_SELECTOR",
                            })
                          }
                          style={{ cursor: "pointer" }}
                        />
                        <div className="qbz-speed-pill">
                          <p className="qbz-speed-pill-text">
                            {audioState.playbackRate}x
                          </p>
                        </div>
                        {audioState.showSpeedSelector &&
                          renderPlaybackSpeedSelector()}
                      </div>
                    </div>

                    {audioState.isWaitingForVoiceConversion ||
                    audioState.isWaitingForAssistantResponse ? (
                      <div className="qbz-status-block">
                        <BaseLoading
                          size="small"
                          primaryColor="#57C2D3"
                          secondaryColor="#57C2D3"
                        />
                        <p className="qbz-status-text-blue">
                          {i18n.t(
                            audioState.isWaitingForVoiceConversion
                              ? "analyzing_your_voice"
                              : "thinking_of_an_answer",
                          )}
                        </p>
                      </div>
                    ) : recordingIsActive ? (
                      <div className="qbz-status-block">
                        <Icon name="start-audio-red" size={40} />
                        <p className="qbz-status-text-red">
                          {i18n.t("recording_release_to_send")}
                        </p>
                      </div>
                    ) : (
                      <div
                        className={`qbz-hold ${
                          chatLoading || !streamReady
                            ? "qbz-hold-disabled"
                            : "qbz-hold-enabled"
                        }`}
                        onMouseDown={handleHoldToTalkStart}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          handleHoldToTalkStart();
                        }}
                      >
                        <Icon name="start-audio" size={40} />
                        <p className="qbz-status-text-blue">
                          {i18n.t("hold_to_talk")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
        <audio
          ref={audioComponentRef}
          className="qbz-hidden"
          crossOrigin="anonymous"
          onEnded={() => {
            dispatchAudio({ type: "END_PLAYBACK" });
          }}
        />
      </div>
    </>
  );
};

export default ChatBubble;
